import type { AttachmentTemplateTracingType } from '@/api/(admin)/attachment-templates/types';
import type {
  AttachmentTemplatesParams,
  AttachmentTemplatesQueryInput,
} from '../-types';

/** The three values `ListQuerySchema`'s `tracingType` enum accepts. */
const TRACING_TYPES = new Set<string>([
  'PRODUCT_BACKWARD',
  'FIBER_FORWARD',
  'FIBER_PRODUCT_TRANSFORMATION',
]);

/** `count` is `z.coerce.number().int().min(1).max(100)` — 101 is a 400. */
export const MAX_COUNT = 100;

/**
 * Turns table state into the endpoint's parameters.
 *
 * Single conversion point, as on the other management lists: the query key is
 * what this returns, so the route loader (reading the URL) and the page's hook
 * (reading `useTable`) only share a cache entry if both derive their params the
 * same way.
 *
 * **`page` is passed through, not decremented.** This is the one place this
 * list differs from `materials` and `standards`, whose routes are 0-based and
 * so apply `Math.max(0, page - 1)`. Here `listTemplatesDb` computes
 * `offset = Math.max(0, (page - 1) * count)`, so the wire value is 1-based and
 * subtracting first would make page 1 and page 2 return the same rows and put
 * the last page out of reach — exactly the bug the old dashboard shipped, whose
 * `useTable` was 0-based.
 *
 * `count` is clamped rather than forwarded: the table footer offers 500 and the
 * route caps at 100, so an unclamped value would 400 the whole list.
 *
 * Deliberately absent: `sort`, `filter` and `q`. The old page wired a sort
 * header and a column filter onto every column and a 500-row page size, and zod
 * stripped all of it — the results were always `ORDER BY UPDATED_AT DESC`. The
 * two parameters the route does support, `name` and `tracingType`, were the
 * ones it never wired up; they are the search box and the facet here.
 */
export function toAttachmentTemplatesParams(
  input: AttachmentTemplatesQueryInput,
): AttachmentTemplatesParams {
  const name = (input.search ?? '').trim();

  // Dropped rather than forwarded: a value outside the enum would 400 the list.
  const rawType = input.filters?.tracingType?.[0];
  const tracingType =
    rawType && TRACING_TYPES.has(rawType)
      ? (rawType as AttachmentTemplateTracingType)
      : undefined;

  return {
    page: Math.max(1, input.page),
    count: Math.min(Math.max(1, input.limit), MAX_COUNT),
    ...(name ? { name } : {}),
    ...(tracingType ? { tracingType } : {}),
  };
}

export function buildAttachmentTemplatesQs(
  params: AttachmentTemplatesParams,
): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  if (params.name) qs.set('name', params.name);
  if (params.tracingType) qs.set('tracingType', params.tracingType);
  return qs.toString();
}
